import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import WarningIcon from "@mui/icons-material/Warning";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import SosSection from "components/SosSection";
import ImageModal from "components/ImageModal";
import EmptyData from "components/EmptyData";
import { getCurrentAdminState } from "Utils/Functions";
import apiClient from "../../Utils/apiClient";
import socketService from "../../socket/newsocketService";

// Legacy Firebase implementation kept for reference during the backend migration:
// import { getDatabase, ref, get, remove } from "firebase/database";
// const usersRef = ref(getDatabase(), "users");
// const whistleRef = ref(db, `users/${userId}/Whistle_Blow/${Id}`);

const REPORTS_PAGE_SIZE = 500;

const classNames = ["btn-danger", "btn-warning", "btn-info", "btn-secondary"];

const whistleCategories = [
  { title: "Kidnapping", color: "#F25F5C" },
  { title: "Cultism", color: "#8B5CF6" },
  { title: "Terrorism", color: "#F97316" },
  { title: "Human Trafficking", color: "#D97706" },
  { title: "Armed Robbery", color: "#2563EB" },
  { title: "Murder / Homicide", color: "#EF4444" },
  { title: "All", color: "#CA1DAE", icon: "warning" },
];

const columns = [
  { Header: <span style={{ color: "blue" }}>SR</span>, accessor: "sr", align: "left" },
  { Header: <span style={{ color: "blue" }}>Type</span>, accessor: "Type", align: "left" },
  { Header: <span style={{ color: "blue" }}>Detail</span>, accessor: "Detail", align: "left" },
  { Header: <span style={{ color: "blue" }}>Sender</span>, accessor: "userName", align: "left" },
  { Header: <span style={{ color: "blue" }}>Evidence</span>, accessor: "userImage", align: "left" },
  { Header: <span style={{ color: "blue" }}>Time Stamp</span>, accessor: "time", align: "center" },
  { Header: <span style={{ color: "blue" }}>Status</span>, accessor: "Status", align: "center" },
  { Header: <span style={{ color: "blue" }}>Action</span>, accessor: "Action", align: "center" },
  { Header: <span style={{ color: "blue" }}>Delete</span>, accessor: "Delete", align: "center" },
  { Header: <span style={{ color: "blue" }}>Location</span>, accessor: "Location", align: "center" },
];

const normalizeText = (value) => String(value ?? "").trim();

const getApiOrigin = () => {
  try {
    return new URL(apiClient?.defaults?.baseURL || "").origin;
  } catch (error) {
    return "";
  }
};

const resolveImageUrl = (value) => {
  const imageUrl = normalizeText(value);

  if (!imageUrl) {
    return "";
  }

  if (/^https?:\/\//i.test(imageUrl) || imageUrl.startsWith("data:")) {
    return imageUrl;
  }

  const apiOrigin = getApiOrigin();
  if (!apiOrigin) {
    return imageUrl;
  }

  return imageUrl.startsWith("/")
    ? `${apiOrigin}${imageUrl}`
    : `${apiOrigin}/${imageUrl}`;
};

const IMAGE_FIELD_PRIORITY = [
  "evidenceImage",
  "evidenceImageUrl",
  "evidenceUrl",
  "evidencePhoto",
  "evidencePicture",
  "evidenceFile",
  "evidence",
  "attachmentUrl",
  "attachment",
  "attachments",
  "photoUrl",
  "photo",
  "pictureUrl",
  "picture",
  "imageUrl",
  "image",
  "reportImage",
  "fileUrl",
  "file",
  "userImage",
];

const IMAGE_KEY_HINTS = /(image|photo|picture|pic|file|attachment|evidence|screenshot|media|url)/i;

const looksLikeImageSource = (value) => {
  const normalized = normalizeText(value);

  if (!normalized) {
    return false;
  }

  return (
    /^https?:\/\//i.test(normalized) ||
    normalized.startsWith("data:") ||
    normalized.startsWith("blob:") ||
    normalized.startsWith("/") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)(?:[?#].*)?$/i.test(normalized)
  );
};

const looksLikeImagePath = (value) => {
  const normalized = normalizeText(value);

  return (
    Boolean(normalized) &&
    !/\s/.test(normalized) &&
    (normalized.includes("/") ||
      normalized.includes("\\") ||
      normalized.includes(".") ||
      normalized.startsWith("gs://") ||
      normalized.startsWith("file:"))
  );
};

const findEvidenceImageValue = (value, parentKeyMatches = false, seen = new WeakSet()) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return looksLikeImageSource(value) || (parentKeyMatches && looksLikeImagePath(value)) ? value : "";
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const candidate = findEvidenceImageValue(item, parentKeyMatches, seen);
      if (candidate) {
        return candidate;
      }
    }

    return "";
  }

  if (typeof value !== "object") {
    return "";
  }

  if (seen.has(value)) {
    return "";
  }

  seen.add(value);

  for (const key of IMAGE_FIELD_PRIORITY) {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      const candidate = findEvidenceImageValue(value[key], true, seen);
      if (candidate) {
        return candidate;
      }
    }
  }

  for (const [key, nestedValue] of Object.entries(value)) {
    const keyMatches = parentKeyMatches || IMAGE_KEY_HINTS.test(key);

    if (typeof nestedValue === "string") {
      const candidate = findEvidenceImageValue(nestedValue, keyMatches, seen);
      if (candidate) {
        return candidate;
      }
      continue;
    }

    const candidate = findEvidenceImageValue(nestedValue, keyMatches, seen);
    if (candidate) {
      return candidate;
    }
  }

  return "";
};

const getWhistleBlowerImage = (report) => resolveImageUrl(findEvidenceImageValue(report));

const getWhistleDateValue = (dateValue) => {
  const value = normalizeText(dateValue);

  if (!value) {
    return 0;
  }

  const parts = value.split(" - ");
  const parsed = parts.length > 1 ? new Date(`${parts[1]} ${parts[0]}`) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const buildWhistleRecords = (reports) =>
  reports
    .map((report) => ({
      id: report?.id || report?._id || "",
      whistleBlowId: report?.id || report?._id || "",
      userId: report?.userId || "",
      whistleBlow: report,
      type: report?.type || "N/A",
      detail: report?.report || report?.detail || report?.description || "Not available",
      time: report?.createdAt ? new Date(report.createdAt).toLocaleString() : "N/A",
      userName: report?.userName || "Anonymous",
      userImage: getWhistleBlowerImage(report),
      latitude: report?.location?.latitude,
      longitude: report?.location?.longitude,
      status: normalizeWhistleStatus(report?.status || report?.Status || report?.isRead),
      sortValue: getWhistleDateValue(report?.createdAt),
    }))
    .sort((a, b) => b.sortValue - a.sortValue);

const buildCounts = (records) => {
  const typeInfo = {};
  const totalCount = records.length;

  records.forEach((record) => {
    const type = record.type || "N/A";
    typeInfo[type] = typeInfo[type] || { count: 0, percentage: 0 };
    typeInfo[type].count += 1;
  });

  Object.keys(typeInfo).forEach((type) => {
    typeInfo[type].percentage = totalCount
      ? Math.floor((typeInfo[type].count / totalCount) * 100)
      : 0;
  });

  return typeInfo;
};

const normalizeWhistleStatus = (value) => {
  if (typeof value === "boolean") {
    return value ? "Approved" : "Submitted";
  }

  const normalized = normalizeText(value).toLowerCase();

  if (!normalized) {
    return "Submitted";
  }

  if (normalized === "pending" || normalized === "new" || normalized === "submitted") {
    return "Submitted";
  }

  if (normalized === "in review" || normalized === "under review" || normalized === "reviewing") {
    return "Under Review";
  }

  if (
    normalized === "seen" ||
    normalized === "read" ||
    normalized === "approved"
  ) {
    return "Approved";
  }

  if (normalized === "resolved" || normalized === "closed") {
    return "Resolved";
  }

  if (normalized === "rejected" || normalized === "dismissed") {
    return "Rejected";
  }

  return normalized
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getWhistleStatusMeta = (status) => {
  const normalized = normalizeWhistleStatus(status).toLowerCase();

  if (normalized === "submitted") {
    return { bg: "#FFF5E5", fg: "#A96C00" };
  }

  if (normalized === "under review") {
    return { bg: "#F3E8FF", fg: "#7C3AED" };
  }

  if (normalized === "approved") {
    return { bg: "#E8F0FE", fg: "#1A56DB" };
  }

  if (normalized === "resolved") {
    return { bg: "#EAF7EF", fg: "#16803C" };
  }

  if (normalized === "rejected") {
    return { bg: "#FDECEC", fg: "#C62828" };
  }

  return { bg: "#F3F4F6", fg: "#6B7280" };
};

function WhistleBlower() {
  const allData = useSelector((state) => state.data.allData);
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("All");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onClickImageData, setOnClickImageData] = useState("");
  const [searchTxt, setSearchText] = useState("");
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuRecord, setStatusMenuRecord] = useState(null);
  const refreshReportsRef = useRef(() => {});

  const handleImageClick = (url) => {
    const imageUrl = resolveImageUrl(url);

    if (!imageUrl) {
      return;
    }

    setIsModalOpen(true);
    setOnClickImageData(imageUrl);
  };

  const onClose = () => {
    setIsModalOpen(false);
  };

  const getActiveState = useCallback(async () => {
    if (allData) {
      return "";
    }

    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    return normalizeText(storedUser?.state || storedUser?.userState) || getCurrentAdminState();
  }, [allData]);

  const getWhistleBlow = useCallback(async () => {
    setLoading(true);

    try {
      const activeState = await getActiveState();
      const reports = [];
      let page = 1;
      let totalPages = 1;

      console.log("[WhistleBlower] get data started", { activeState });

      do {
        const params = {
          page,
          limit: REPORTS_PAGE_SIZE,
        };

        if (activeState) {
          params.state = activeState;
        }

        if (search !== "All") {
          params.type = search;
        }

        if (searchTxt) {
          params.search = searchTxt;
        }

        const response = await apiClient.get("/whistle-blower", { params });
        const pageReports = Array.isArray(response?.data) ? response.data : [];
        const pagination = response?.pagination || {};

        console.log("[WhistleBlower] /whistle-blower response", {
          page,
          reportCount: pageReports.length,
          pagination,
        });

        reports.push(...pageReports);
        totalPages = Number(pagination?.totalPages || 1);
        page += 1;
      } while (page <= totalPages);

      const whistleRecords = buildWhistleRecords(reports);

      console.log("[WhistleBlower] filtered whistle blower data", {
        backendReports: reports.length,
        reportCount: whistleRecords.length,
        reports: whistleRecords,
      });

      setRecords(whistleRecords);
    } catch (error) {
      console.log("[WhistleBlower] failed to get data", error);
      toast.error(error?.message || "Error fetching whistle blower reports.");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [getActiveState, search, searchTxt]);

  useEffect(() => {
    refreshReportsRef.current = getWhistleBlow;
  }, [getWhistleBlow]);

  useEffect(() => {
    const user = localStorage.getItem("user");
    
    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }

    const currentUser = JSON.parse(user);
    socketService.disconnect();
    void socketService
      .initializeSocket(
        currentUser?._id || currentUser?.userId || currentUser?.key || null,
        currentUser?.state || currentUser?.userState || "",
        {
          isSuperAdmin: Boolean(currentUser?.superAdmin),
          web: "true",
        },
      )
      .catch((error) => {
        console.log("[WhistleBlower] socket connect failed", error);
      });

    const handleWhistleBlowerEvent = (payload) => {
      console.log("[WhistleBlower] realtime whistleblower event", payload);
      void refreshReportsRef.current();
    };

    socketService.on("whistleblower", handleWhistleBlowerEvent);

    return () => {
      socketService.off("whistleblower", handleWhistleBlowerEvent);
      socketService.disconnect();
    };
  }, [navigate]);

  useEffect(() => {
    void getWhistleBlow();
  }, [getWhistleBlow]);

  const counts = useMemo(() => buildCounts(records), [records]);

  const deleteItem = useCallback(
    async (reportId) => {
      const target = records.find((record) => record.id === reportId);

      if (!target) {
        return;
      }

      try {
        await apiClient.delete(`/whistle-blower/${reportId}`);
        setRecords((currentRecords) =>
          currentRecords.filter((record) => record.id !== reportId),
        );
        toast.success("Whistle blower report deleted successfully.");
      } catch (error) {
        toast.error(error?.message || "Error deleting whistle blower report.");
      }
    },
    [records],
  );

  const updateReportStatus = useCallback(
    async (reportId, nextStatus) => {
      const normalizedStatus = normalizeWhistleStatus(nextStatus);
      const statusPayload = { status: normalizedStatus };
      const endpoints = [
        () => apiClient.patch(`/whistle-blower/${reportId}/status`, statusPayload),
        () => apiClient.patch(`/whistle-blower/${reportId}`, statusPayload),
        () => apiClient.put(`/whistle-blower/${reportId}/status`, statusPayload),
      ];

      let lastError = null;

      for (const request of endpoints) {
        try {
          await request();
          setRecords((currentRecords) =>
            currentRecords.map((record) =>
              record.id === reportId ? { ...record, status: normalizedStatus } : record,
            ),
          );
          toast.success(`Status updated to ${normalizedStatus}.`);
          return;
        } catch (error) {
          lastError = error;
        }
      }

      toast.error(lastError?.message || "Unable to update whistle blower status.");
    },
    [],
  );

  const handleOpenStatusMenu = useCallback((event, record) => {
    setStatusMenuAnchor(event.currentTarget);
    setStatusMenuRecord(record);
  }, []);

  const handleCloseStatusMenu = useCallback(() => {
    setStatusMenuAnchor(null);
    setStatusMenuRecord(null);
  }, []);

  const handleStatusChange = useCallback(
    async (nextStatus) => {
      if (!statusMenuRecord?.id) {
        handleCloseStatusMenu();
        return;
      }

      const reportId = statusMenuRecord.id;
      handleCloseStatusMenu();
      await updateReportStatus(reportId, nextStatus);
    },
    [handleCloseStatusMenu, statusMenuRecord, updateReportStatus],
  );

  const filteredRecords = useMemo(() => {
    const selectedType = search === "All" ? "" : search;
    const text = searchTxt.toLowerCase();

    return records.filter((record) => {
      const matchesType = !selectedType || record.type === selectedType;
      const matchesText =
        !text ||
        record.userName.toLowerCase().includes(text) ||
        record.type.toLowerCase().includes(text) ||
        record.detail.toLowerCase().includes(text) ||
        record.status.toLowerCase().includes(text);

      return matchesType && matchesText;
    });
  }, [records, search, searchTxt]);

  const rows = useMemo(
    () =>
      filteredRecords.map((record, index) => {
        const randomClass = classNames[index % classNames.length];
        const hasLocation = record.latitude && record.longitude;
        const statusMeta = getWhistleStatusMeta(record.status);

        return {
          sr: index + 1,
          Type: <button className={`btn btn-sm ${randomClass}`}>{record.type}</button>,
          Detail: (
            <Box
              title={record.detail}
              sx={{
                maxWidth: 260,
                fontSize: 11,
                lineHeight: 1.35,
                color: "#4B5563",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              }}
            >
              {record.detail}
            </Box>
          ),
          userName: record.userName,
          userImage: record.userImage ? (
            <img
              onClick={() => handleImageClick(record.userImage)}
              src={record.userImage}
              alt={record.userName}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #E5E7EB",
              }}
            />
          ) : (
            "N/A"
          ),
          time: record.time,
          Status: (
            <Chip
              label={record.status}
              size="small"
              sx={{
                background: statusMeta.bg,
                color: statusMeta.fg,
                fontWeight: 700,
                fontSize: 11,
              }}
            />
          ),
          Action: (
            <Button
              size="small"
              variant="contained"
              onClick={(event) => handleOpenStatusMenu(event, record)}
              sx={{
                minWidth: 124,
                textTransform: "none",
                fontSize: 11,
                fontWeight: 700,
                borderRadius: 999,
                boxShadow: "none",
                backgroundColor: "#1F6FEB",
                "&:hover": {
                  backgroundColor: "#1557C0",
                  boxShadow: "none",
                },
              }}
            >
              Change Status
            </Button>
          ),
          Delete: (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => deleteItem(record.id)}
            >
              Delete
            </button>
          ),
          Location: hasLocation ? (
            <button
              onClick={() => navigate(`/LocateSos/${record.latitude}/${record.longitude}`)}
              className="btn btn-danger btn-sm"
            >
              Track
            </button>
          ) : (
            "N/A"
          ),
          whistleBlow: record.whistleBlow,
        };
      }),
    [deleteItem, filteredRecords, handleOpenStatusMenu, navigate],
  );

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar value={searchTxt} onChange={handleSearchChange} />
      <ToastContainer />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6} style={{ position: "relative", minHeight: "60vh" }}>
          {loading ? (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Loading...
            </div>
          ) : (
            <>
              <div className="container row ms-4 mb-1">
                {whistleCategories.map((category) => (
                  <div className="col-sm col-lg col-xl mt-2" key={category.title}>
                    <SosSection
                      OnPress={() => setSearch(category.title)}
                      count={
                        category.title === "All"
                          ? records.length
                          : counts[category.title]?.count || 0
                      }
                      per={counts[category.title]?.percentage || 0}
                      color={category.color}
                      icon={
                        category.icon === "warning" ? (
                          <WarningIcon style={{ color: "white" }} />
                        ) : (
                          <DriveEtaIcon style={{ color: "white" }} />
                        )
                      }
                      title={category.title}
                    />
                  </div>
                ))}
              </div>

              <Grid item xs={12}>
                <Card>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                  >
                    <MDTypography variant="h6" color="white">
                      Dashboard
                    </MDTypography>
                  </MDBox>

                  <MDBox pt={3}>
                    {rows.length > 0 ? (
                      <DataTable
                        table={{ columns, rows }}
                        isSorted={false}
                        entriesPerPage={false}
                        showTotalEntries={false}
                        noEndBorder
                      />
                    ) : (
                      <EmptyData />
                    )}
                  </MDBox>
                </Card>

                <ImageModal
                  onClickImageData={onClickImageData}
                  onClose={onClose}
                  isModalOpen={isModalOpen}
                />

                <Menu
                  anchorEl={statusMenuAnchor}
                  open={Boolean(statusMenuAnchor && statusMenuRecord)}
                  onClose={handleCloseStatusMenu}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
          {["Submitted", "Under Review", "Approved", "Resolved", "Rejected"].map((statusOption) => (
                    <MenuItem
                      key={statusOption}
                      onClick={() => handleStatusChange(statusOption)}
                      disabled={normalizeWhistleStatus(statusMenuRecord?.status) === statusOption}
                    >
                      {statusOption}
                    </MenuItem>
                  ))}
                </Menu>
              </Grid>
            </>
          )}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default WhistleBlower;
